import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import usePermissions from "../hooks/usePermissions";
import { authHeaders } from "../utils/session";

/* ─── Scoped Styles ───────────────────────────────────────────────────────── */
const styles = `
  .msp-wrap {
    display: flex;
    gap: 0;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 16px;
    box-shadow: 0 2px 16px rgba(28,36,75,0.07);
    overflow: hidden;
    min-height: 520px;
  }

  /* ── Left Panel ── */
  .msp-left {
    width: 248px;
    flex-shrink: 0;
    border-right: 1px solid #e8eaf0;
    background: #fafbff;
    display: flex;
    flex-direction: column;
  }
  .msp-left-header {
    padding: 18px 18px 14px;
    font-size: 15px;
    font-weight: 800;
    color: #1a1f36;
    letter-spacing: -0.2px;
    border-bottom: 1px solid #e8eaf0;
  }
  /* ── Group section headers ── */
  .msp-group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 8px;
    margin-bottom: 4px;
    margin-top: 4px;
  }
  .msp-group-header-attorney {
    background: linear-gradient(135deg, #eef1fb, #dde3f8);
    border: 1px solid #c5cff5;
  }
  .msp-group-header-corporate {
    background: linear-gradient(135deg, #fff8ec, #fdefd4);
    border: 1px solid #fad9a0;
  }
  .msp-group-icon {
    width: 26px; height: 26px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; flex-shrink: 0;
  }
  .msp-group-icon-attorney { background: #3b5bdb; }
  .msp-group-icon-corporate { background: #e07b00; }
  .msp-group-text { flex: 1; }
  .msp-group-title {
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    line-height: 1.2;
  }
  .msp-group-title-attorney { color: #3b5bdb; }
  .msp-group-title-corporate { color: #c46b00; }
  .msp-group-count {
    font-size: 10px; font-weight: 700;
    padding: 2px 8px; border-radius: 10px;
  }
  .msp-group-count-attorney { background: #3b5bdb; color: #fff; }
  .msp-group-count-corporate { background: #e07b00; color: #fff; }

  /* ── Cards — attorney gets blue accent, corporate gets orange ── */
  .msp-plan-card-attorney { border-left: 3px solid #c5cff5 !important; }
  .msp-plan-card-attorney:hover { border-left-color: #3b5bdb !important; }
  .msp-plan-card-attorney.active { border-left-color: #3b5bdb !important; }
  .msp-plan-card-corporate { border-left: 3px solid #fad9a0 !important; }
  .msp-plan-card-corporate:hover { border-left-color: #e07b00 !important; }
  .msp-plan-card-corporate.active {
    background: #fff8ec !important;
    border-color: #e07b00 !important;
    border-left-color: #e07b00 !important;
  }
  .msp-plan-card-corporate.active .msp-plan-name { color: #c46b00 !important; }

  .msp-group-divider {
    border: none;
    border-top: 1px solid #e8eaf0;
    margin: 10px 0 6px;
  }
  .msp-plan-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .msp-plan-card {
    padding: 12px 14px;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    border: 1.5px solid transparent;
  }
  .msp-plan-card:hover { background: #eef1fb; border-color: #d0d8f5; }
  .msp-plan-card.active { background: #eef1fb; border-color: #3b5bdb; }
  .msp-plan-name { font-size: 13.5px; font-weight: 700; color: #1a1f36; margin-bottom: 3px; }
  .msp-plan-card.active .msp-plan-name { color: #3b5bdb; }
  .msp-plan-desc { font-size: 11.5px; color: #8b92a9; font-weight: 500; line-height: 1.4; }
  .msp-card-footer {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 8px;
  }
  .msp-card-actions { display: flex; gap: 5px; }
  .msp-action-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 24px; height: 24px; border-radius: 6px;
    border: 1.5px solid transparent; font-size: 10px;
    cursor: pointer; text-decoration: none; transition: all 0.15s ease;
  }
  .msp-btn-edit { background: #eef1fb; border-color: #d0d8f5; color: #3b5bdb; }
  .msp-btn-edit:hover { background: #3b5bdb; border-color: #3b5bdb; color: #fff !important; text-decoration: none; }
  .msp-btn-delete { background: #fff1f0; border-color: #ffc9c6; color: #d92b2b; }
  .msp-btn-delete:hover { background: #d92b2b; border-color: #d92b2b; color: #fff; }

  /* ── Status badge ── */
  .msp-status {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 8px; border-radius: 6px; font-size: 10.5px; font-weight: 700;
  }
  .msp-status-active { background: #edfaf3; color: #0a8a4a; border: 1px solid #b8f0d4; cursor: pointer; }
  .msp-status-inactive { background: #fff1f0; color: #d92b2b; border: 1px solid #ffc9c6; cursor: pointer; }
  .msp-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
  .msp-dot-a { background: #0a8a4a; }
  .msp-dot-i { background: #d92b2b; }

  /* ── Right Panel ── */
  .msp-right {
    flex: 1;
    padding: 26px 30px;
    overflow-y: auto;
  }
  .msp-right-title {
    font-size: 17px; font-weight: 800; color: #1a1f36;
    margin-bottom: 6px; letter-spacing: -0.3px;
  }
  .msp-right-desc {
    font-size: 13px; color: #8b92a9; font-weight: 500;
    margin-bottom: 22px; line-height: 1.5;
  }

  /* ── Sections ── */
  .msp-section { margin-bottom: 24px; }
  .msp-section-label {
    font-size: 11.5px; font-weight: 700; color: #8b92a9;
    text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 12px;
  }

  /* ── Pricing cards ── */
  .msp-pricing-row { display: flex; gap: 12px; }
  .msp-price-card {
    flex: 1;
    padding: 14px 16px;
    border: 1.5px solid #e8eaf0;
    border-radius: 10px;
    background: #fafbff;
  }
  .msp-price-card-label { font-size: 11px; font-weight: 600; color: #8b92a9; text-transform: uppercase; letter-spacing: 0.6px; margin-bottom: 6px; }
  .msp-price-amount { font-size: 20px; font-weight: 800; color: #1a1f36; letter-spacing: -0.5px; }
  .msp-price-currency { font-size: 13px; font-weight: 600; color: #8b92a9; margin-right: 1px; }
  /* Sits beside the amount, quiet enough not to compete with it. */
  .msp-price-unit { font-size: 11.5px; font-weight: 600; color: #8b92a9; letter-spacing: 0; margin-left: 6px; }
  .msp-price-offer {
    display: inline-flex; align-items: center; gap: 4px;
    margin-top: 5px; padding: 2px 8px;
    background: #fff8ec; color: #e07b00; border: 1px solid #fde8b8;
    border-radius: 6px; font-size: 11px; font-weight: 700;
  }

  /* ── Badges row ── */
  .msp-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 20px; }
  .msp-badge {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 4px 12px; border-radius: 8px; font-size: 12px; font-weight: 700;
  }
  .msp-badge-attorney { background: #eef1fb; color: #3b5bdb; border: 1px solid #d0d8f5; }
  .msp-badge-corporate { background: #fff8ec; color: #e07b00; border: 1px solid #fde8b8; }
  .msp-badge-trial { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }

  /* ── Numeric features ── */
  .msp-limits { display: flex; flex-direction: column; gap: 6px; }
  .msp-limit-row {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 9px 12px;
    border: 1px solid #f0f2f8; border-radius: 8px; background: #fafbff;
  }
  .msp-limit-key { font-size: 13px; font-weight: 500; color: #4b5680; flex: 1; }
  .msp-limit-val { font-size: 13px; font-weight: 700; color: #1a1f36; white-space: nowrap; }

  /* ── Boolean features ── */
  .msp-features-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }
  .msp-feature-item {
    display: flex; align-items: flex-start; gap: 8px;
    padding: 8px 10px; border: 1px solid #f0f2f8;
    border-radius: 8px; background: #fafbff;
  }
  .msp-check {
    width: 18px; height: 18px; border-radius: 5px;
    background: #edfaf3; border: 1.5px solid #6ee7b7;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0; margin-top: 1px;
    font-size: 10px; color: #0a8a4a; font-weight: 700;
  }
  .msp-feature-name { font-size: 12.5px; font-weight: 500; color: #374151; line-height: 1.4; }

  /* ── Page header ── */
  .msp-page-header {
    display: flex; align-items: center; justify-content: space-between;
    margin-bottom: 20px; padding-bottom: 18px; border-bottom: 1px solid #f0f2f7;
  }
  .msp-page-title { font-size: 20px; font-weight: 800; color: #1a1f36; letter-spacing: -0.3px; margin: 0; }
  .msp-add-btn {
    display: inline-flex; align-items: center; gap: 7px;
    background: linear-gradient(135deg, #3b5bdb 0%, #4c6ef5 100%);
    color: #fff !important; font-size: 13.5px; font-weight: 700;
    padding: 10px 20px; border-radius: 10px; text-decoration: none !important;
    border: none; box-shadow: 0 4px 14px rgba(59,91,219,0.3); transition: all 0.2s ease;
  }
  .msp-add-btn:hover {
    transform: translateY(-2px); box-shadow: 0 8px 20px rgba(59,91,219,0.4);
    color: #fff !important; text-decoration: none !important;
  }

  /* ── Empty ── */
  .msp-empty {
    display: flex; flex-direction: column; align-items: center;
    justify-content: center; padding: 60px 24px;
    color: #8b92a9; font-size: 14px; gap: 10px;
  }
  .msp-empty-icon { font-size: 36px; opacity: 0.3; }

  @media (max-width: 640px) {
    .msp-features-grid { grid-template-columns: 1fr; }
    .msp-pricing-row { flex-direction: column; }
  }
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const ManageSubPlanNew = () => {
  const { canCreate, canEdit, canDelete } = usePermissions("/cms/manage-subscription-plan");

  const [planData, setPlanData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => { fetchAllPlans(); }, []);

  const fetchAllPlans = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/get-all-plans-v1`, {
        headers: { ...authHeaders() },
      });
      const data = res.data.data || [];
      setPlanData(data);
      if (data.length > 0) setSelectedId(data[0].id);
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to change the status?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, change it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      Swal.showLoading();
      try {
        const res = await axios.post(
          `${API_BASE_URL}/plans/${id}/status`,
          { is_active: newStatus },
          { headers: { ...authHeaders() } }
        );
        if (res.data.status == "success") {
          Swal.fire("Changed!", "The status has been updated.", "success");
          fetchAllPlans();
        }
      } catch (err) {
        console.log(err);
        Swal.fire("Error!", "Failed to change status.", "error");
      } finally { Swal.hideLoading(); }
    });
  };

  const handleDeletePlan = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete the plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      Swal.showLoading();
      try {
        const res = await axios.post(
          `${API_BASE_URL}/delete-plans/${id}`,
          {},
          { headers: { ...authHeaders() } }
        );
        if (res.data.status == "success") {
          Swal.fire("Deleted!", "The plan has been deleted.", "success");
          fetchAllPlans();
        }
      } catch (err) {
        console.log(err);
        Swal.fire("Error!", "Failed to delete plan.", "error");
      } finally { Swal.hideLoading(); }
    });
  };

  const selectedPlan = planData.find((p) => p.id === selectedId) || null;

  /* Split features by type */
  const numericFeatures = (selectedPlan?.features || []).filter(f => f.feature_type === "numeric");
  const booleanFeatures = (selectedPlan?.features || []).filter(f => f.feature_type === "boolean");

  const fmt = (v) => {
    if (v == null || v === "") return "—";
    const n = Number(v);
    return isNaN(n) ? v : n.toLocaleString("en-IN");
  };

  return (
    <>
      <style>{styles}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">

            {/* Page Header */}
            <div className="msp-page-header">
              <h4 className="msp-page-title">Manage Subscription Plans</h4>
              {canCreate && (
                <Link to="/cms/manage-plan-new/add-subscription-plan-new" className="msp-add-btn">
                  + Add Plan
                </Link>
              )}
            </div>

            {/* Two-panel layout */}
            <div className="msp-wrap">

              {/* ── Left: Plan list ── */}
              <div className="msp-left">
                <div className="msp-left-header">Plans</div>
                <div className="msp-plan-list">
                  {isLoading ? (
                    <div style={{ padding: "24px 8px", color: "#8b92a9", fontSize: 13, textAlign: "center" }}>Loading…</div>
                  ) : planData.length === 0 ? (
                    <div style={{ padding: "24px 8px", color: "#8b92a9", fontSize: 13, textAlign: "center" }}>No plans found.</div>
                  ) : (() => {
                    const attorneyPlans = planData.filter(p => p.is_for_attorney == 1);
                    const corporatePlans = planData.filter(p => p.is_for_attorney == 0);

                    const renderCard = (plan) => (
                      <div
                        key={plan.id}
                        className={`msp-plan-card ${plan.is_for_attorney == 1 ? "msp-plan-card-attorney" : "msp-plan-card-corporate"}${selectedId === plan.id ? " active" : ""}`}
                        onClick={() => setSelectedId(plan.id)}
                      >
                        <div className="msp-plan-name">{plan.name}</div>
                        {plan.description && (
                          <div className="msp-plan-desc">{plan.description}</div>
                        )}
                        <div className="msp-card-footer">
                          {plan.is_active == 1 ? (
                            <span
                              className="msp-status msp-status-active"
                              title="Click to deactivate"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(plan.id, plan.is_active); }}
                            >
                              <span className="msp-dot msp-dot-a" /> Active
                            </span>
                          ) : (
                            <span
                              className="msp-status msp-status-inactive"
                              title="Click to activate"
                              onClick={(e) => { e.stopPropagation(); handleStatusChange(plan.id, plan.is_active); }}
                            >
                              <span className="msp-dot msp-dot-i" /> Inactive
                            </span>
                          )}
                          <div className="msp-card-actions">
                            {canEdit && (
                              <Link
                                to={`/cms/manage-plan-new/edit-subscription-plan-new/${plan.id}`}
                                className="msp-action-btn msp-btn-edit"
                                title="Edit"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="fa fa-pen" />
                              </Link>
                            )}
                            {canDelete && (
                              <button
                                className="msp-action-btn msp-btn-delete"
                                title="Delete"
                                onClick={(e) => { e.stopPropagation(); handleDeletePlan(plan.id); }}
                              >
                                <i className="fa fa-trash" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );

                    return (
                      <>
                        {/* Attorney / IP Professional group */}
                        {attorneyPlans.length > 0 && (
                          <>
                            <div className="msp-group-header msp-group-header-attorney">
                              <div className="msp-group-icon msp-group-icon-attorney">⚖️</div>
                              <div className="msp-group-text">
                                <div className="msp-group-title msp-group-title-attorney">Attorney / IP Professional</div>
                              </div>
                              <span className="msp-group-count msp-group-count-attorney">{attorneyPlans.length}</span>
                            </div>
                            {attorneyPlans.map(renderCard)}
                          </>
                        )}

                        {/* Corporate group */}
                        {corporatePlans.length > 0 && (
                          <>
                            {attorneyPlans.length > 0 && <hr className="msp-group-divider" />}
                            <div className="msp-group-header msp-group-header-corporate">
                              <div className="msp-group-icon msp-group-icon-corporate">🏢</div>
                              <div className="msp-group-text">
                                <div className="msp-group-title msp-group-title-corporate">Corporate</div>
                              </div>
                              <span className="msp-group-count msp-group-count-corporate">{corporatePlans.length}</span>
                            </div>
                            {corporatePlans.map(renderCard)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* ── Right: Plan details ── */}
              <div className="msp-right">
                {!selectedPlan ? (
                  <div className="msp-empty">
                    <div className="msp-empty-icon">📋</div>
                    <span>Select a plan to view its configuration</span>
                  </div>
                ) : (
                  <>
                    {/* Title */}
                    <div className="msp-right-title">{selectedPlan.name} Configuration</div>
                    {selectedPlan.description && (
                      <div className="msp-right-desc">{selectedPlan.description}</div>
                    )}

                    {/* Type + Free Trial badges */}
                    <div className="msp-badges">
                      <span className={`msp-badge ${selectedPlan.is_for_attorney == 1 ? "msp-badge-attorney" : "msp-badge-corporate"}`}>
                        {selectedPlan.is_for_attorney == 1 ? "Attorney / IP Professional" : "Corporate"}
                      </span>
                      {/* {selectedPlan.is_free_trial == 1 && (
                        <span className="msp-badge msp-badge-trial">
                          ✓ Free Trial {selectedPlan.free_trial_days > 0 ? `— ${selectedPlan.free_trial_days} days` : ""}
                        </span>
                      )} */}
                    </div>

                    {/* ── Pricing ── */}
                    <div className="msp-section">
                      <div className="msp-section-label">Pricing</div>
                      <div className="msp-pricing-row">
                        <div className="msp-price-card">
                          <div className="msp-price-card-label">Monthly</div>
                          <div className="msp-price-amount">
                            <span className="msp-price-currency">₹</span>{fmt(selectedPlan.monthly_price)}
                            {/* The figure alone read as the whole plan price; it is
                                charged per trademark, on both cycles. */}
                            <span className="msp-price-unit">per trademark</span>
                          </div>
                        </div>
                        <div className="msp-price-card">
                          <div className="msp-price-card-label">Annual</div>
                          <div className="msp-price-amount">
                            <span className="msp-price-currency">₹</span>{fmt(selectedPlan.annual_price)}
                            <span className="msp-price-unit">per trademark</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ── Numeric Features (Limits) ── */}
                    {numericFeatures.length > 0 && (
                      <div className="msp-section">
                        <div className="msp-section-label">Plan Limits</div>
                        <div className="msp-limits">
                          {numericFeatures.map((f) => (
                            <div className="msp-limit-row" key={f.id}>
                              <span className="msp-limit-key">{f.name}</span>
                              <span className="msp-limit-val">{fmt(f.default_value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── Boolean Features (Included) ── */}
                    {booleanFeatures.length > 0 && (
                      <div className="msp-section">
                        <div className="msp-section-label">Included Features</div>
                        <div className="msp-features-grid">
                          {booleanFeatures.map((f) => (
                            <div className="msp-feature-item" key={f.id}>
                              <div className="msp-check">✓</div>
                              <span className="msp-feature-name">{f.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageSubPlanNew;
